/** The landing's reach section: how one check reaches every student, whatever the room has.
 *
 * The four ways in follow the coverage ladder (docs/PLAN.md 4.6): phones in the browser
 * (tier 3, and tier 2 over the teacher's hotspot), clickers (tier 1), printed cards (tier 0),
 * plus the Telegram channel. Each says what it needs and how ready it is, and READINESS is the
 * one place that decides the second: a page that sells a prototype as a product is lying.
 *
 *   phones    today      the browser join and the Teacher Computer app's local host ship
 *   clickers  coming     STORY-084..090: protocol bench and prototype, no device ships yet
 *   cards     trial      STORY-089: built behind the `cards` flag, on by default on a local host
 *   telegram  perServer  built (STORY-096..129); each server needs a bot, and answering in the
 *                        chat waits on that server's data-protection decision (OWNER-ACTIONS 8)
 *
 * Readiness is text and a glyph, never colour alone. The cards share subgrid rows, so the
 * drawings, headings, bodies and readiness lines start on one line across a row of cards
 * however long a translation runs.
 */
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

import { CardArt, ChatArt, ClickerArt, PhonesArt } from "./Art";

export type Readiness = "today" | "trial" | "coming" | "perServer";
export type Way = "phones" | "clickers" | "cards" | "telegram";

export const READINESS: Record<Way, Readiness> = {
  phones: "today",
  clickers: "coming",
  cards: "trial",
  telegram: "perServer",
};

const ART: Record<Way, ComponentType> = { phones: PhonesArt, clickers: ClickerArt, cards: CardArt, telegram: ChatArt };

export const WAYS: readonly Way[] = ["phones", "clickers", "cards", "telegram"];

/** Solid when it works today, half when it is being tried, empty when it is still to come. */
const GLYPH: Record<Readiness, string> = { today: "●", trial: "◐", coming: "○", perServer: "◌" };

export function Reach() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="reach-title" className="mx-auto max-w-5xl px-6 pb-16 md:px-10">
      <h2 id="reach-title" className="display max-w-3xl text-2xl sm:text-3xl">
        {t("home.reach.title")} <span className="marker">{t("home.reach.titleMark")}</span>
      </h2>
      <p className="mt-3 max-w-prose text-[color:var(--ink-2)]">{t("home.reach.lede")}</p>
      <ul className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {WAYS.map((way) => {
          const Art = ART[way];
          const ready = READINESS[way];
          return (
            <li
              key={way}
              data-way={way}
              data-readiness={ready}
              className="row-span-5 grid min-w-0 grid-rows-subgrid gap-3 rounded-[var(--radius-card)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 shadow-sm"
            >
              <Art />
              <h3 className="display text-lg">{t(`home.reach.${way}.title`)}</h3>
              <p className="text-[color:var(--ink-2)]">{t(`home.reach.${way}.body`)}</p>
              <p className="text-sm">
                <span className="font-semibold">{t("home.reach.needs")}</span> {t(`home.reach.${way}.needs`)}
              </p>
              <p className="flex items-start gap-2 self-end border-t border-[color:var(--line)] pt-3 text-sm font-medium">
                <span aria-hidden className="leading-5">{GLYPH[ready]}</span>
                <span>
                  {t(`home.reach.readiness.${ready}`)}
                  <span className="block font-normal text-[color:var(--ink-2)]">{t(`home.reach.${way}.status`)}</span>
                </span>
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
